import { useEffect, useRef } from 'react';
import { MapLocationData } from '@hero/web-components';

export interface UseFitAllButtonOptions {
  /** Google Maps instance */
  map: google.maps.Map | null;
  /** Locations to fit bounds to */
  locations: MapLocationData[];
  /** Function to fit bounds */
  onFitBounds: (map: google.maps.Map, locations: MapLocationData[]) => void;
  /** Whether the button should be visible */
  visible?: boolean;
  /** Button position on the map */
  position?: google.maps.ControlPosition;
}

/**
 * Hook to manage Fit All Locations button on Google Maps
 * Creates a custom control button that fits all locations to bounds
 */
export function useFitAllButton({
  map,
  locations,
  onFitBounds,
  visible = true,
  position = google.maps.ControlPosition.RIGHT_BOTTOM,
}: UseFitAllButtonOptions) {
  const fitAllButtonRef = useRef<HTMLButtonElement | null>(null);
  const locationsRef = useRef<MapLocationData[]>(locations);
  const onFitBoundsRef = useRef(onFitBounds);
  const mapRef = useRef<google.maps.Map | null>(map);

  // Keep refs updated
  useEffect(() => {
    locationsRef.current = locations;
  }, [locations]);

  useEffect(() => {
    onFitBoundsRef.current = onFitBounds;
  }, [onFitBounds]);

  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  // Create and add button to map
  useEffect(() => {
    if (!map) return;

    // Only add the button once
    if (!fitAllButtonRef.current) {
      // Create custom Fit All control button
      const controlButton = document.createElement('button');
      controlButton.style.backgroundColor = '#4F46E5'; // Primary brand color
      controlButton.style.border = '0';
      controlButton.style.borderRadius = '50%';
      controlButton.style.boxShadow = '0 2px 6px rgba(79, 70, 229, 0.4)'; // Brand color shadow
      controlButton.style.cursor = 'pointer';
      controlButton.style.margin = '10px';
      controlButton.style.padding = '0';
      controlButton.style.width = '40px';
      controlButton.style.height = '40px';
      controlButton.style.display = 'flex';
      controlButton.style.alignItems = 'center';
      controlButton.style.justifyContent = 'center';
      controlButton.style.transition = 'all 0.2s ease';
      controlButton.title = 'View All Locations';
      controlButton.type = 'button';

      // Add hover effect
      controlButton.addEventListener('mouseenter', () => {
        controlButton.style.backgroundColor = '#4338CA'; // Darker on hover
        controlButton.style.boxShadow = '0 4px 8px rgba(79, 70, 229, 0.5)';
        controlButton.style.transform = 'scale(1.05)';
      });
      controlButton.addEventListener('mouseleave', () => {
        controlButton.style.backgroundColor = '#4F46E5';
        controlButton.style.boxShadow = '0 2px 6px rgba(79, 70, 229, 0.4)';
        controlButton.style.transform = 'scale(1)';
      });

      // Create icon (Maximize2 SVG)
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('width', '20');
      icon.setAttribute('height', '20');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('fill', 'none');
      icon.setAttribute('stroke', '#ffffff'); // White icon
      icon.setAttribute('stroke-width', '2');
      icon.setAttribute('stroke-linecap', 'round');
      icon.setAttribute('stroke-linejoin', 'round');
      icon.innerHTML =
        '<polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line>';

      controlButton.appendChild(icon);

      // Add click handler that uses refs to always get latest values
      controlButton.onclick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const currentMap = mapRef.current;
        const currentLocations = locationsRef.current;
        const currentOnFitBounds = onFitBoundsRef.current;
        
        if (!currentMap || currentLocations.length === 0 || !currentOnFitBounds) {
          return;
        }
        
        // Call the fit bounds function
        currentOnFitBounds(currentMap, currentLocations);
      };

      // Store in ref
      fitAllButtonRef.current = controlButton;

      // Add to map controls
      map.controls[position].push(controlButton);
    }

    // Cleanup: remove button when component unmounts
    return () => {
      if (fitAllButtonRef.current && map) {
        const controls = map.controls[position];
        const index = controls.getArray().indexOf(fitAllButtonRef.current);
        if (index > -1) {
          controls.removeAt(index);
        }
        fitAllButtonRef.current = null;
      }
    };
  }, [map, position]);

  // Update button visibility when locations or visible prop changes
  useEffect(() => {
    if (fitAllButtonRef.current) {
      // Show/hide button based on visible prop and locations
      const shouldShow = visible && locations.length > 0;
      if (shouldShow) {
        fitAllButtonRef.current.style.display = 'flex';
        fitAllButtonRef.current.style.opacity = '1';
        fitAllButtonRef.current.style.pointerEvents = 'auto';
      } else {
        fitAllButtonRef.current.style.display = 'none';
        fitAllButtonRef.current.style.opacity = '0';
        fitAllButtonRef.current.style.pointerEvents = 'none';
      }
    }
  }, [visible, locations.length]);
}
